import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box';
import AntPage from './AntPage';
import AntWidgetGrid from './AntWidgetGrid';
import AntWidgetPivotGrid from './AntWidgetPivotGrid';
import AntWidgetChart from './AntWidgetChart';
import AntWidgetPieChart from './AntWidgetPieChart';
import AntWidgetStatisticGroup from './AntWidgetStatisticGroup';
import AntWidgetInfoPanel from './AntWidgetInfoPanel';
import AntWidgetMetricCard from './AntWidgetMetricCard';
import AntWidgetProgressList from './AntWidgetProgressList';
import AntWidgetTimeline from './AntWidgetTimeline';
import AntWidgetGauge from './AntWidgetGauge';
import AntWidgetHeatmap from './AntWidgetHeatmap';
import AntWidgetCalendarHeatmap from './AntWidgetCalendarHeatmap';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';

import AntAccordion from './AntAccordion';
import AntAPI from '../utils/AntAPI';
import AntErrorPage from './AntErrorPage';

import cs from '../utils/const';
import { AntGlobal, AntSession } from '../utils/AntContext';
import './AntDashboard.scss';

export default function (props) {
    const appGlobal = AntGlobal();
    // const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    
    const [mainItems, setMainItems] = useState([]);

    const isRender = AntCondition.isRender({}, props);

    const loadContent = async (widgets) => {
        var items = widgets
            .map((r, index) => {
                var wConfig = r.config;
                var isVisible = AntCondition.isVisible(null, wConfig);
                if (!isVisible) return null;

                return {
                    ...r.config,
                    widget_group: r.group,
                    widget_code: r.code,
                    widget_name: r.name,
                    widget_type: r.type
                };
            })
            .filter((r) => Boolean(r));

        setMainItems(items);
    };

    useEffect(() => {
        loadContent(props.widgets);
    }, [props.widgets]);

    // Widget rỗng KHÔNG bị gỡ khỏi lưới nữa: mỗi widget tự hiện "Chưa có dữ liệu"
    // trong đúng khung của nó, nhờ vậy lưới không sắp lại sau khi tải xong.
    var visibleItems = mainItems;

    // ─── Pre-calculate row layout & equalize heights ───
    const layoutInfo = useMemo(() => {
        if (!visibleItems || visibleItems.length === 0) return { rows: [], itemMap: [] };

        // Default heights by widget type (px)
        var defaultHeights = {
            Grid: 280, Chart: 300, DoughnutChart: 300, PivotGrid: 350,
            Timeline: 280, InfoPanel: 260, ProgressList: 260, Gauge: 220,
            Heatmap: 260, CalendarHeatmap: 260,
            StatisticGroup: null, MetricCard: null,
            Custom: 360
        };

        // Chiều cao giữ chỗ cho 2 loại gọn — nhỉnh hơn chiều cao thật một chút
        // để ô không phình thêm khi dữ liệu về (chúng vẫn tự co giãn, không bị ép height).
        var compactReserved = { StatisticGroup: 115, MetricCard: 92 };

        // Build row groups based on cumulative colspan
        var rows = [];        // array of { widgets: [...] }
        var itemMap = [];     // per-widget: { lgRowIndex, lgColIndex, rowHeight }
        var currentRow = [];
        var currentSpan = 0;

        for (let i = 0; i < visibleItems.length; i++) {
            var span = visibleItems[i].colspan || 1;
            if (currentSpan + span > 10 && currentRow.length > 0) {
                rows.push({ widgets: currentRow });
                currentRow = [];
                currentSpan = 0;
            }
            currentRow.push({ index: i, span, colStart: currentSpan });
            currentSpan += span;
            if (currentSpan >= 10) {
                rows.push({ widgets: currentRow });
                currentRow = [];
                currentSpan = 0;
            }
        }
        if (currentRow.length > 0) rows.push({ widgets: currentRow });

        // Calculate max height per row for equalization
        for (let r = 0; r < rows.length; r++) {
            var row = rows[r];
            var maxH = 0;
            var hasFixedHeight = false;
            for (let w = 0; w < row.widgets.length; w++) {
                var item = visibleItems[row.widgets[w].index];
                var h = item.height || defaultHeights[item.widget_type] || 0;
                if (h > 0) { hasFixedHeight = true; maxH = Math.max(maxH, h); }
            }
            // Only equalize rows that have at least one sized widget and multiple widgets
            var equalizeRow = hasFixedHeight && row.widgets.length > 1;
            for (let w = 0; w < row.widgets.length; w++) {
                var wInfo = row.widgets[w];
                var wItem = visibleItems[wInfo.index];
                var wType = wItem.widget_type;
                var isCompact = wType === 'StatisticGroup' || wType === 'MetricCard';
                var ownHeight = wItem.height || defaultHeights[wType] || 0;
                itemMap[wInfo.index] = {
                    lgRowIndex: r,
                    lgColIndex: wInfo.colStart,
                    // Compact widgets don't get forced height — they auto-size
                    rowHeight: (equalizeRow && !isCompact) ? maxH : null,
                    // Chiều cao sàn của khung bọc: đặt ngay từ lượt vẽ đầu để lưới đứng yên
                    reservedHeight: isCompact
                        ? (wItem.height || compactReserved[wType] || 0)
                        : (equalizeRow ? maxH : ownHeight)
                };
            }
        }

        return { rows, itemMap };
    }, [visibleItems]);

    var totalRows = layoutInfo.rows.length || 1;

    return (
        <React.Fragment>
            <AntPage className="ant-dashboard-page" hideBackgroundImage={!isRender}>
                {!isRender && (
                    <AntErrorPage
                        id={`ant-dashboard`}
                        text={AntLocalization.translateText({ vi: 'Không có quyền truy cập !', en: 'Access Denied', fr: 'Accès refusé', es: 'Acceso denegado', id: 'Akses ditolak', ja: 'アクセス拒否', zh: '访问被拒绝', ko: '접근 거부', hi: 'पहुँच अस्वीकृत', ar: 'تم رفض الوصول' })}
                    />
                )}

                {isRender && (
                    <ResponsiveBox
                        singleColumnScreen="sm"
                        screenByWidth={(width) => {
                            return width < 1000 ? 'sm' : 'lg';
                        }}
                    >
                        <Col ratio={1}></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>
                        <Col ratio={1} screen="lg"></Col>

                        {Array.from({ length: Math.max(totalRows, visibleItems.length) }, (_, i) => (
                            <Row ratio={1} key={i}></Row>
                        ))}

                        {visibleItems.map((child, index) => {
                            var info = layoutInfo.itemMap[index] || {};
                            var lgRowIndex = info.lgRowIndex != null ? info.lgRowIndex : 0;
                            var lgColIndex = info.lgColIndex != null ? info.lgColIndex : 0;
                            var rowHeight = info.rowHeight;
                            var wrapStyle = { marginLeft: 2, marginRight: 2 };
                            if (info.reservedHeight) wrapStyle.minHeight = info.reservedHeight;

                            return (
                                <Item key={index}>
                                    <Location row={lgRowIndex} col={lgColIndex} colspan={child.colspan ? child.colspan : 1} screen="lg"></Location>
                                    <Location row={index} col={0} colspan={1} screen="sm"></Location>

                                    {child.widget_type === 'Grid' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetGrid
                                                widgetCode={child.widget_code}
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                table={child.table}
                                                title={child.title}
                                                height={rowHeight || child.height}
                                                colspan={child.colspan}
                                                columns={child.columns}
                                                url={child.url}
                                                keyExpr={child.keyExpr}
                                                showDetail={child.showDetail}
                                                searchConditions={child.searchConditions}
                                                sortable={child.sortable}
                                                remotePaging={false}
                                                allowedPageSizes={child.allowedPageSizes}
                                                actionItems={child.actionItems}
                                                showActionBar={child.showActionBar}
                                                showFieldChooser={child.showFieldChooser}
                                                refreshIntervalSec={child.refreshIntervalSec}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'Chart' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetChart
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                table={child.table}
                                                title={child.title}
                                                height={rowHeight || child.height}
                                                colspan={child.colspan}
                                                columns={child.columns}
                                                url={child.url}
                                                searchConditions={child.searchConditions}
                                                actionItems={child.actionItems}
                                                showActionBar={child.showActionBar}
                                                entityName={child.entityName}
                                                entityDisplay={child.entityDisplay}
                                                argumentField={child.argumentField}
                                                argumentFormat={child.argumentFormat}
                                                queryFields={child.queryFields}
                                                seriesTemplate={child.seriesTemplate}
                                                commonSeriesSettings={child.commonSeriesSettings}
                                                series1={child.series1}
                                                series2={child.series2}
                                                series3={child.series3}
                                                series4={child.series4}
                                                valueAxis={child.valueAxis}
                                                argumentAxis={child.argumentAxis}
                                                showLegend={child.showLegend}
                                                legend={child.legend}
                                                rotated={child.rotated}
                                                palette={child.palette}
                                                refreshIntervalSec={child.refreshIntervalSec}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'DoughnutChart' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetPieChart
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                table={child.table}
                                                title={child.title}
                                                height={rowHeight || child.height}
                                                colspan={child.colspan}
                                                columns={child.columns}
                                                url={child.url}
                                                searchConditions={child.searchConditions}
                                                actionItems={child.actionItems}
                                                showActionBar={child.showActionBar}
                                                entityName={child.entityName}
                                                entityDisplay={child.entityDisplay}
                                                argumentField={child.argumentField}
                                                argumentFormat={child.argumentFormat}
                                                queryFields={child.queryFields}
                                                seriesTemplate={child.seriesTemplate}
                                                commonSeriesSettings={child.commonSeriesSettings}
                                                series1={child.series1}
                                                series2={child.series2}
                                                series3={child.series3}
                                                valueAxis={child.valueAxis}
                                                argumentAxis={child.argumentAxis}
                                                legend={child.legend}
                                                refreshIntervalSec={child.refreshIntervalSec}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'PivotGrid' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetPivotGrid
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                table={child.table}
                                                title={child.title}
                                                colspan={child.colspan}
                                                columns={child.columns}
                                                url={child.url}
                                                chart={child.chart}
                                                {...child.pivotGrid}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'StatisticGroup' && child.items instanceof Array && child.items.length > 0 && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetStatisticGroup
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                table={child.table}
                                                title={child.title}
                                                items={child.items}
                                                url={child.url}
                                                refreshIntervalSec={child.refreshIntervalSec}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'InfoPanel' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetInfoPanel
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                url={child.url}
                                                title={child.title}
                                                items={child.items}
                                                footer={child.footer}
                                                showBars={child.showBars}
                                                searchConditions={child.searchConditions}
                                                dataSource={child.dataSource}
                                                labelField={child.labelField}
                                                valueField={child.valueField}
                                                valueDataType={child.valueDataType}
                                                maxItems={child.maxItems}
                                                refreshIntervalSec={child.refreshIntervalSec}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'MetricCard' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetMetricCard
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                url={child.url}
                                                title={child.title}
                                                caption={child.caption}
                                                mainField={child.mainField}
                                                mainDataType={child.mainDataType}
                                                compareField={child.compareField}
                                                compareLabel={child.compareLabel}
                                                changeField={child.changeField}
                                                trendField={child.trendField}
                                                trend={child.trend}
                                                searchConditions={child.searchConditions}
                                                refreshIntervalSec={child.refreshIntervalSec}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'ProgressList' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetProgressList
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                url={child.url}
                                                title={child.title}
                                                labelField={child.labelField}
                                                currentField={child.currentField}
                                                targetField={child.targetField}
                                                valueDataType={child.valueDataType}
                                                showValues={child.showValues}
                                                searchConditions={child.searchConditions}
                                                dataSource={child.dataSource}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'Timeline' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetTimeline
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                url={child.url}
                                                title={child.title}
                                                height={rowHeight || child.height}
                                                timeField={child.timeField}
                                                titleField={child.titleField}
                                                subtitleField={child.subtitleField}
                                                typeField={child.typeField}
                                                maxItems={child.maxItems}
                                                searchConditions={child.searchConditions}
                                                dataSource={child.dataSource}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'Gauge' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetGauge
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                url={child.url}
                                                title={child.title}
                                                items={child.gaugeItems}
                                                valueField={child.valueField}
                                                targetField={child.targetField}
                                                gaugeLabel={child.gaugeLabel}
                                                gaugeColor={child.gaugeColor}
                                                gaugeSize={child.gaugeSize}
                                                strokeWidth={child.strokeWidth}
                                                searchConditions={child.searchConditions}
                                                dataSource={child.dataSource}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'Heatmap' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetHeatmap
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                url={child.url}
                                                title={child.title}
                                                valueField={child.valueField}
                                                searchConditions={child.searchConditions}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'CalendarHeatmap' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            <AntWidgetCalendarHeatmap
                                                API={new AntAPI(child.moduleName, child.entityName, child.entityDisplay)}
                                                url={child.url}
                                                title={child.title}
                                                valueField={child.valueField}
                                                numWeeks={child.numWeeks}
                                                searchConditions={child.searchConditions}
                                            />
                                        </div>
                                    )}

                                    {child.widget_type === 'Custom' && typeof child.component === 'function' && (
                                        <div className="ant-dashboard-widget" style={wrapStyle}>
                                            {React.createElement(child.component, {
                                                ...(child.componentProps || {}),
                                                title: child.title,
                                                height: rowHeight || child.height,
                                                colspan: child.colspan,
                                                widgetCode: child.widget_code
                                            })}
                                        </div>
                                    )}
                                </Item>
                            );
                        })}
                    </ResponsiveBox>
                )}
            </AntPage>
        </React.Fragment>
    );
}

